/* ==========================================================================
   LSC - NYC Clinic pages: behaviour (assets/lsc-clinic.js)
   Created 2026-10-30 for DEV 2.2. Loaded with defer by the LSC Clinic sections
   that need it (sub-nav, treatment finder, service page).

   Three small, independent pieces. Each one finds its own markup by data
   attribute, so a section that is not on the page simply does nothing, and
   each re-initialises when the theme editor reloads a section
   (shopify:section:load), so editing in the customizer keeps working.

     1. Sub-nav dropdowns  [data-lsc-subnav]
     2. Treatment finder   [data-lsc-matcher]
     3. Phone booking bar  [data-lsc-bookbar] watching [data-lsc-bookcard]
   ========================================================================== */
(function () {
  'use strict';
  // LSC - 2026-11-02: run once per page. Several clinic sections load this file
  // (finder, service page), and a page with more than one would otherwise
  // register the observers twice.
  if (window.lscClinicLoaded) return;
  window.lscClinicLoaded = true;

  /* ------------------------------------------------------------------------
     1. Sub-nav dropdowns. A parent item is a <button aria-expanded> followed
        by a <ul hidden> panel. One panel open at a time; Escape and a click
        outside close it. On phones the CSS hides panels and the parent link
        beside the button takes over, so nothing here runs differently.
     ------------------------------------------------------------------------ */
  function initSubnav(root) {
    if (!root || root.dataset.lscReady) return;
    root.dataset.lscReady = '1';
    var toggles = root.querySelectorAll('[data-lsc-subnav-toggle]');

    function closeAll(except) {
      toggles.forEach(function (btn) {
        if (btn === except) return;
        btn.setAttribute('aria-expanded', 'false');
        var panel = document.getElementById(btn.getAttribute('aria-controls'));
        if (panel) panel.hidden = true;
      });
    }

    toggles.forEach(function (btn) {
      btn.addEventListener('click', function () {
        var open = btn.getAttribute('aria-expanded') === 'true';
        closeAll(btn);
        btn.setAttribute('aria-expanded', open ? 'false' : 'true');
        var panel = document.getElementById(btn.getAttribute('aria-controls'));
        if (panel) panel.hidden = open;
      });
    });

    document.addEventListener('click', function (e) {
      if (!root.contains(e.target)) closeAll();
    });
    root.addEventListener('keydown', function (e) {
      if (e.key !== 'Escape') return;
      var openBtn = root.querySelector('[data-lsc-subnav-toggle][aria-expanded="true"]');
      closeAll();
      if (openBtn) openBtn.focus();
    });
  }

  /* ------------------------------------------------------------------------
     2. Treatment finder. Each concern chip is a <button aria-pressed> with
        data-lsc-match="<block id>"; each result is a panel with the same id
        in data-lsc-result. Without JavaScript the first result shows, which
        is the server-rendered state.
     ------------------------------------------------------------------------ */
  function initMatcher(root) {
    if (!root || root.dataset.lscReady) return;
    root.dataset.lscReady = '1';
    var chips = root.querySelectorAll('[data-lsc-match]');
    var results = root.querySelectorAll('[data-lsc-result]');

    function select(id) {
      chips.forEach(function (chip) {
        chip.setAttribute('aria-pressed', chip.dataset.lscMatch === id ? 'true' : 'false');
      });
      results.forEach(function (panel) {
        panel.hidden = panel.dataset.lscResult !== id;
      });
    }

    chips.forEach(function (chip) {
      chip.addEventListener('click', function () { select(chip.dataset.lscMatch); });
    });
  }

  /* ------------------------------------------------------------------------
     3. Phone booking bar. Shows once the booking card has scrolled out of
        view above the viewport, and hides again when it comes back. CSS
        hides the bar from 990px, where the card itself is sticky.
     ------------------------------------------------------------------------ */
  function initBookbar(bar) {
    if (!bar || bar.dataset.lscReady || !('IntersectionObserver' in window)) return;
    bar.dataset.lscReady = '1';
    var card = document.querySelector('[data-lsc-bookcard]');
    if (!card) return;
    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        var above = !entry.isIntersecting && entry.boundingClientRect.top < 0;
        bar.classList.toggle('is-visible', above);
        bar.setAttribute('aria-hidden', above ? 'false' : 'true');
      });
    });
    io.observe(card);
  }

  /*
   * LSC - 2026-10-30: sticky offsets.
   * Sets two CSS variables on <html> that lsc-clinic.css uses to place the
   * sticky sub-nav, the sticky booking card and in-page jump targets:
   *   --lsc-header-offset  height of the theme header while it is sticky and
   *                        showing, otherwise 0. Concept hides the header on
   *                        scroll down (it adds .header-hidden to the header
   *                        section and slides the header up), on phones even
   *                        with "Sticky header: always", so the offset follows
   *                        that class and the sub-nav moves up into the space.
   *   --lsc-subnav-h       height of the sticky clinic sub-nav, or 0.
   * Also adds .lsc-clinic-subnav-section--sticky to the sub-nav's section
   * wrapper, for browsers that do not support :has().
   * Re-measured when the header or the sub-nav changes size (ResizeObserver),
   * so the values follow the phone and desktop header heights.
   */
  var lscObserver = null;
  var lscClassObserver = null;
  function measureOffsets() {
    var root = document.documentElement;
    var header = document.querySelector('.header-section');
    var headerH = 0;
    if (header && !header.classList.contains('header-hidden')) {
      var pos = getComputedStyle(header).position;
      if (pos === 'sticky' || pos === 'fixed') headerH = header.getBoundingClientRect().height;
    }
    var nav = document.querySelector('.lsc-clinic-subnav--sticky');
    var navH = nav ? nav.getBoundingClientRect().height : 0;
    root.style.setProperty('--lsc-header-offset', Math.round(headerH) + 'px');
    root.style.setProperty('--lsc-subnav-h', Math.round(navH) + 'px');
    if (nav && nav.parentElement) nav.parentElement.classList.add('lsc-clinic-subnav-section--sticky');
  }
  function initOffsets() {
    measureOffsets();
    if (!('ResizeObserver' in window)) {
      window.addEventListener('resize', measureOffsets);
      return;
    }
    if (lscObserver) lscObserver.disconnect();
    lscObserver = new ResizeObserver(measureOffsets);
    var header = document.querySelector('.header-section');
    var nav = document.querySelector('.lsc-clinic-subnav');
    if (header) lscObserver.observe(header);
    if (nav) lscObserver.observe(nav);
    // The header showing or hiding on scroll is a class change, not a resize.
    if (header && 'MutationObserver' in window) {
      if (lscClassObserver) lscClassObserver.disconnect();
      lscClassObserver = new MutationObserver(measureOffsets);
      lscClassObserver.observe(header, { attributes: true, attributeFilter: ['class'] });
    }
  }

  function initAll(scope) {
    var s = scope || document;
    initOffsets();
    s.querySelectorAll('[data-lsc-subnav]').forEach(initSubnav);
    s.querySelectorAll('[data-lsc-matcher]').forEach(initMatcher);
    s.querySelectorAll('[data-lsc-bookbar]').forEach(initBookbar);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { initAll(); });
  } else {
    initAll();
  }

  // Theme editor: re-initialise a section after it is re-rendered.
  document.addEventListener('shopify:section:load', function (e) { initAll(e.target); });
})();
